import {CdkDragDrop} from '@angular/cdk/drag-drop';

export type TeamRoleKind = 'leader' | 'member';

export type TeamLoadState = 'pending' | 'loaded' | 'error';

export type OrganizeListId = 'workspace-members' | 'leaders' | 'members';

export interface TeamRole {
  uuid: string;
  name: string;
}

export interface TeamRoles {
  leader: TeamRole;
  member: TeamRole;
}

export interface OrganizeMember {
  userUuid: string;
  email: string;
  displayName: string;
  label: string;
  avatarUrl: string | null;
  // Only reliable when the member comes from the workspace-users or search endpoints:
  // the team-members endpoint always returns 0 for the start-guide counters.
  hasServerAssignment: boolean;
}

export interface TeamAssignment {
  userUuid: string;
  role: TeamRoleKind;
  // null while the assignment only exists locally (not saved yet).
  originalRole: TeamRoleKind | null;
  membershipUuid: string | null;
}

export interface TeamType {
  slug: string;
  title: string;
}

// Shape of the team objects placed in DataManipulationService.organizeTeams by the callers.
export interface SourceTeam {
  uuid: string;
  name: string;
  totalMembers?: number;
  leaders?: number;
  members?: number;
}

export interface LoadedTeam {
  teamUuid: string;
  teamType: TeamType | null;
  members: OrganizeMember[];
  assignments: TeamAssignment[];
}

export interface TeamLoadOutcome {
  teamUuid: string;
  team: LoadedTeam | null;
}

export interface MembersPage {
  members: OrganizeMember[];
  page: number;
  pageCount: number;
}

export interface OrganizeTeamView {
  uuid: string;
  name: string;
  initial: string;
  teamType: TeamType | null;
  leaderCount: number;
  memberCount: number;
  loadState: TeamLoadState;
}

export interface WorkspaceMemberRow {
  member: OrganizeMember;
  assigned: boolean;
  checked: boolean;
}

export interface SaveChange {
  kind: 'add' | 'changeRole';
  teamUuid: string;
  userUuid: string;
  role: TeamRoleKind;
}

export interface SaveResult {
  change: SaveChange;
  ok: boolean;
  // Set for successful adds: the new membership and whether the backend activated it.
  membershipUuid: string | null;
  active: boolean;
  errorMessage: string | null;
}

export interface RemoveMemberEvent {
  userUuid: string;
  role: TeamRoleKind;
}

export interface OrganizeDropEvent {
  sourceListId: string;
  targetListId: string;
  previousIndex: number;
  currentIndex: number;
  member: OrganizeMember;
}

// Every drag item in the organize-teams drop lists carries its OrganizeMember as cdkDragData.
export function toOrganizeDropEvent<T>(event: CdkDragDrop<T>): OrganizeDropEvent {
  return {
    sourceListId: event.previousContainer.id,
    targetListId: event.container.id,
    previousIndex: event.previousIndex,
    currentIndex: event.currentIndex,
    member: event.item.data
  };
}
